import { CheckOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, InputRef, MenuProps } from 'antd';
import OwnerDropdown from '../Dropdown/OwnerDropdown';
import { FiltersButton } from './FiltersButton';
import { Filter } from '../../Modal/TaskFiltersModal';
import { useContext, useEffect, useRef, useState } from 'react';
import { WorkspaceView, WorkspaceViewEntityType, WorkspaceViewGroup } from '../../../types/WorkspaceView';
import ViewDisplayDropdown from '../Dropdown/ViewDisplayDropdown';
import { BaseButton } from '../../UI/BaseButton';
import { useViewManagement } from '../../../hooks/useViewManagement';
import { ModalContext, ModalType } from '../../../context/ModalContext';

import { TaskType } from '../../../types/TaskType';
interface FiltersHeaderProps {
  menuProps?: MenuProps | undefined;
  activeView: WorkspaceView;
  planningSessionId?: number | null;
  ownerFilter: Filter | undefined;
  onSaveOwnerFilter: (ownerFilter: Filter) => void;
  onClearOwnerFilter: () => void;
  isViewSelectedFilters: boolean;
  selectedFiltersLength: number;
  onFiltersClick: () => void;
  searchValue: string;
  setSearchValue: (searchValue: string) => void;
  createTask?: () => void;
  showMessage: (message: string) => void;
  resetToSavedFilters: () => void;
  saveFiltersForAll: () => void;
}

export const FiltersHeader: React.FC<FiltersHeaderProps> = ({
  menuProps,
  activeView,
  planningSessionId,
  ownerFilter,
  onSaveOwnerFilter,
  onClearOwnerFilter,
  isViewSelectedFilters,
  selectedFiltersLength,
  onFiltersClick,
  searchValue,
  setSearchValue,
  createTask,
  showMessage,
  resetToSavedFilters,
  saveFiltersForAll,
}) => {
  // Initialize expanded when a search is already active so the field width
  // survives navigating into a task and back (it remounts with a value). #2495
  const [searchWidth, setSearchWidth] = useState<string>(() => (searchValue ? '300px' : '96px'));
  const searchInputRef = useRef<InputRef>(null);

  const { openModal } = useContext(ModalContext);

  const { isViewSelectedConfig, resetToSavedView, handleSaveViewForAll } = useViewManagement(activeView, () =>
    showMessage('View config saved')
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        if (document.activeElement === searchInputRef.current?.input) return;
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.input?.select();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const onSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const resetToSaved = () => {
    if (!isViewSelectedConfig) {
      resetToSavedView();
    }
  };

  const saveForAll = () => {
    if (!isViewSelectedConfig) {
      handleSaveViewForAll(activeView);
    }
  };

  const isArchiveView = activeView.group === WorkspaceViewGroup.archive;

  return (
    <div className="w-full flex justify-between items-center">
      <div className="flex align-middle items-center gap-2">
          {!isArchiveView && (
            <Button
              type="primary"
              className="flex items-center gap-1"
              onClick={() =>
                openModal(ModalType.CreateItem, {
                  taskType: activeView.entityType === WorkspaceViewEntityType.epic ? TaskType.epic : TaskType.task,
                })
              }
              style={{
                width: 'initial',
                backgroundColor: 'var(--color-blue-600)',
                borderColor: 'var(--color-blue-600)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-blue-500)';
                e.currentTarget.style.borderColor = 'var(--color-blue-500)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-blue-600)';
                e.currentTarget.style.borderColor = 'var(--color-blue-600)';
              }}
            >
              <PlusOutlined className="text-white" />
              <div className="text-white text-sm font-semibold">Create New</div>
            </Button>
          )}

        <OwnerDropdown
          filter={ownerFilter}
          onSave={(ownerFilter) => onSaveOwnerFilter(ownerFilter)}
          onClear={onClearOwnerFilter}
        />
        <FiltersButton
          isViewSelectedFilters={isViewSelectedFilters}
          selectedFiltersLength={selectedFiltersLength}
          onClick={onFiltersClick}
        />
        <Input
          ref={searchInputRef}
          placeholder="Search"
          prefix={<SearchOutlined className="mr-1" />}
          allowClear={searchValue.length > 0 ? true : false}
          className="[&_input::placeholder]:!text-neutral-900 px-3 text-neutral-900 mr-auto"
          style={{ width: searchWidth }}
          onFocus={() => {
            setSearchWidth('300px');
          }}
          onBlur={() => {
            if (!searchValue) {
              setSearchWidth('96px');
            }
          }}
          value={searchValue}
          onChange={onSearchInput}
        />
      </div>

      <div className="flex gap-2">
        <ViewDisplayDropdown
          view={activeView}
          isViewSelectedConfig={isViewSelectedConfig}
          onSave={saveForAll}
          onClear={resetToSaved}
        />
      </div>
    </div>
  );
};
